import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, ExternalLink } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { addGitAccount, normalizeServer } from '../../services/git/gitAccounts';
import { openExternal } from '../../utils/openExternal';
import ProviderIcon from './ProviderIcon';

// "gitlab" → "Gitlab": the suffix of the per-provider strings (subtitleGitea, tokenHelpGithub …).
const cap = (id) => id.charAt(0).toUpperCase() + id.slice(1);

/** Where to make a token: the provider's page on the server typed so far, or '' while it is not an address yet. */
function tokenPageFor(provider, server) {
  if (!provider.tokenPage) return '';
  if (!provider.selfHosted) return provider.tokenPage();
  try {
    return provider.tokenPage(normalizeServer(server || provider.baseUrl));
  } catch {
    return '';
  }
}

/**
 * Add an account for a provider that signs in with a token: GitLab (gitlab.com
 * or self-managed — card 6 step 2), Gitea / Forgejo / Codeberg, and GitHub by
 * personal access token (07.10.2026). The token is checked against the server
 * before anything is kept, and then goes to the Apiboo Vault, never to a file —
 * and never to Apiboo: it works signed out of Apiboo.
 */
export default function AddGitAccountModal({ provider, isOpen, onClose, onAdded }) {
  const { t } = useTranslation();
  const [server, setServer] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setServer(provider?.baseUrl || '');
      setToken('');
      setError('');
      setBusy(false);
    }
  }, [isOpen, provider]);

  if (!isOpen || !provider) return null;
  const suffix = cap(provider.id);
  const isGitHub = provider.id === 'github';
  const tokenPage = tokenPageFor(provider, server);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const account = await addGitAccount({
        provider: provider.id,
        server: server || provider.baseUrl,
        token,
        verify: (base, secret) => provider.verifyToken(base, secret),
        single: Boolean(provider.singleAccount),
      });
      onAdded?.(account);
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 401 || status === 403 ? t('gitAccounts.rejected') : err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isGitHub ? t('gitAccounts.githubTitle') : t('gitAccounts.menuItem', { provider: provider.label })}
      subtitle={t(`gitAccounts.subtitle${suffix}`, { defaultValue: '' }) || undefined}
      customIcon={<ProviderIcon id={provider.id} size={20} />}
      preventBackdropClose={busy}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="add-git-account">
        {provider.selfHosted && (
          <div>
            <label htmlFor="git-account-server" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
              {t('gitAccounts.server')}
            </label>
            <input
              id="git-account-server"
              type="url"
              value={server}
              onChange={(e) => setServer(e.target.value)}
              disabled={busy}
              placeholder={provider.serverPlaceholder || provider.baseUrl}
              className="modal-input"
            />
          </div>
        )}
        <div>
          <label htmlFor="git-account-token" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
            {t('gitAccounts.token')}
          </label>
          <input
            id="git-account-token"
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={busy}
            className="modal-input"
          />
          <p className="text-xs" style={{ opacity: 0.7, marginTop: 6 }}>
            {t(`gitAccounts.tokenHelp${suffix}`, { defaultValue: provider.tokenHelp || '' })}
            {tokenPage && (
              <>
                {' '}
                <button type="button" className="git-token-link" onClick={() => openExternal(tokenPage)}>
                  {t('gitAccounts.createToken')}
                  <ExternalLink size={11} aria-hidden="true" />
                </button>
              </>
            )}
          </p>
          <p className="text-xs" style={{ opacity: 0.7, marginTop: 4 }}>{t('gitAccounts.keychain')}</p>
        </div>
        {error && (
          <p className="text-xs" role="status" aria-live="polite" style={{ color: 'var(--danger, #ef4444)' }}>
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !token.trim() || (provider.selfHosted && !server.trim())}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy && <Loader className="animate-spin" size={16} />}
          <span>{busy ? t('gitAccounts.checking') : t('gitAccounts.submit')}</span>
        </button>
      </form>
    </Modal>
  );
}
